import { serializeParams } from "@/utils/apiUtils";
import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../common/api/ApiRequestAdapter";
import { apiService } from "../../common/api/apiService";
import { HttpContentType, HttpHeaders, HttpMethod } from "../../common/api/http";
import {
  EntityObject,
  ThreeLevelEntityIdObject,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../common/types";
import { ContractAttachment } from "../contract/types";
import { DocSignatureView } from "./enums";
import {
  DocSignature,
  DocSignatureFilterPageRequest,
  DocSignatureFilterPageResult,
  InitDocSignature,
  TokenAccessDocSignature,
} from "./types";

const CREATE_DOCUMENT_SIGNATURE = ApiRequestAdapter.create("/clients/{0}/doc-signatures", HttpMethod.POST);
const FILTER_DOCUMENTS_TO_SIGN_FOR_CLIENT = ApiRequestAdapter.create("/clients/{0}/doc-signatures", HttpMethod.GET);
const DOWNLOAD_ORIGINAL_DOCUMENT = ApiRequestAdapter.create(
  "/clients/{0}/doc-signatures/{1}/original-file",
  HttpMethod.GET,
);
const DOWNLOAD_SIGNED_DOCUMENT = ApiRequestAdapter.create(
  "/clients/{0}/doc-signatures/{1}/signed-file",
  HttpMethod.GET,
);
const DOWNLOAD_ORIGINAL_DOCUMENT_THROUGH_EMAIL = ApiRequestAdapter.create(
  "/clients/doc-signatures/original-file",
  HttpMethod.POST,
);
const CONFIRM_EMAIL_SIGNATURE = ApiRequestAdapter.create("/clients/doc-signatures/confirm", HttpMethod.POST);
const RESEND_SIGNATURE_NOTIFICATION = ApiRequestAdapter.create(
  "/clients/{0}/doc-signatures/{1}/resend",
  HttpMethod.POST,
);
const DELETE_DOCUMENT_SIGNATURE = ApiRequestAdapter.create("/clients/{0}/doc-signatures/{1}", HttpMethod.DELETE);
const ASSIGN_SIGNED_DOCUMENT_TO_CONTRACT = ApiRequestAdapter.create(
  "/clients/{0}/doc-signatures/{1}/contracts/{2}/assign",
  HttpMethod.POST,
);

export const documentsSignApi = {
  createDocumentSignature: (request: EntityObject<FormData>): AxiosPromise<DocSignature> => {
    return apiService.post(CREATE_DOCUMENT_SIGNATURE.getParametrizedUrl(request.id), request.object, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  filterDocumentsToSignForClient: (
    request: EntityObject<DocSignatureFilterPageRequest & { view: DocSignatureView }>,
  ): AxiosPromise<DocSignatureFilterPageResult> => {
    return apiService.get(FILTER_DOCUMENTS_TO_SIGN_FOR_CLIENT.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  downloadOriginalDocument: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_ORIGINAL_DOCUMENT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob",
    });
  },
  downloadSignedDocument: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_SIGNED_DOCUMENT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob",
    });
  },
  downloadOriginalDocumentThroughEmail: (request: TokenAccessDocSignature): AxiosPromise<Blob> => {
    return apiService.post(DOWNLOAD_ORIGINAL_DOCUMENT_THROUGH_EMAIL.url, request, { responseType: "blob" });
  },
  confirmEmailSignature: (request: TokenAccessDocSignature): AxiosPromise => {
    return apiService.post(CONFIRM_EMAIL_SIGNATURE.url, request);
  },
  resendDocumentSignature: (request: TwoLevelEntityObject<InitDocSignature>): AxiosPromise<DocSignature> => {
    return apiService.post(RESEND_SIGNATURE_NOTIFICATION.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteDocumentSignature: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_DOCUMENT_SIGNATURE.getParametrizedUrl(request.id1, request.id2));
  },
  assignSignedDocumentToContract: (request: ThreeLevelEntityIdObject): AxiosPromise<ContractAttachment> => {
    return apiService.post(
      ASSIGN_SIGNED_DOCUMENT_TO_CONTRACT.getParametrizedUrl(request.id1, request.id2, request.id3),
    );
  },
};
